import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  BookOpen,
  Brain,
  Library,
  Shuffle,
  Sparkles,
  Target,
  MessageCircle,
} from 'lucide-react'
import { learningApi, type LearningSuggestion, type TodayProgress } from '@/api/learning'
import { dashboardApi } from '@/api/dashboard'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { QuietFeedbackCta } from '@/components/feedback/QuietFeedbackCta'
import { useMinCopy } from '@/i18n/useUiLocale'
import type { SessionSummary as SessionSummaryData } from '@/api/review'

interface KeepLearningScreenProps {
  summary?: SessionSummaryData | null
  todayProgress?: TodayProgress | null
  tomorrowDueCount: number
  tomorrowPreviewMessage?: string | null
  growthHighlightMessage?: string | null
  caughtUp?: boolean
  articlesRead?: number
  challengeScore?: number
}

const suggestionIcons: Record<string, typeof BookOpen> = {
  READ_ANOTHER: BookOpen,
  WEAK_WORDS: Target,
  VOCABULARY: Library,
  CHALLENGE: Brain,
  TUTOR: MessageCircle,
  SURPRISE: Shuffle,
}

export function KeepLearningScreen({
  summary,
  todayProgress,
  tomorrowDueCount,
  tomorrowPreviewMessage,
  growthHighlightMessage,
  caughtUp = false,
  articlesRead = 0,
  challengeScore = 0,
}: KeepLearningScreenProps) {
  const { pages } = useMinCopy()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const suggestionsQuery = useQuery({
    queryKey: ['learningSuggestions'],
    queryFn: () => learningApi.getSuggestions(),
    staleTime: 1000 * 60,
  })

  const dashboardQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.getDashboard(),
    staleTime: 0,
  })

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }, [queryClient])

  const handleDone = () => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    navigate('/dashboard')
  }

  const handleSuggestion = (suggestion: LearningSuggestion) => {
    navigate(suggestion.path)
  }

  const isEmptyCaughtUp = caughtUp && !summary
  const title = isEmptyCaughtUp ? "You're all caught up" : 'Guided session complete'
  const description =
    isEmptyCaughtUp
      ? "Today's guided minimum is done. Keep exploring if you have energy."
      : "Great work! You've completed today's guided session."

  const liveToday: TodayProgress = {
    wordsRefreshed:
      dashboardQuery.data?.todayPlan.reviewsCompleted ??
      todayProgress?.wordsRefreshed ??
      summary?.cardsReviewed ??
      0,
    articlesRead:
      dashboardQuery.data?.todayPlan.articlesRead ??
      todayProgress?.articlesRead ??
      articlesRead,
    wordsLearned: todayProgress?.wordsLearned ?? 0,
    testsCompleted: todayProgress?.testsCompleted ?? (challengeScore > 0 ? 1 : 0),
  }

  const today = liveToday

  const sessionLine =
    summary &&
    (summary.cardsReviewed > 0 || articlesRead > 0 || challengeScore > 0)
      ? `This session: ${summary.cardsReviewed} refreshed · ${articlesRead} read${
          challengeScore > 0 ? ` · ${challengeScore}% challenge` : ''
        }`
      : null

  return (
    <Card
      className="mx-auto w-full max-w-2xl overflow-x-hidden"
      data-testid="keep-learning-screen"
    >
      <CardHeader className="text-center sm:text-left">
        {isEmptyCaughtUp ? (
          <>
            <div className="mb-2 flex justify-center sm:justify-start">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                {pages.continueLearning}
              </span>
            </div>
            <CardTitle className="text-2xl">{title}</CardTitle>
          </>
        ) : null}
        <CardDescription className={isEmptyCaughtUp ? undefined : 'text-base text-foreground'}>
          {description}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Today so far
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Words refreshed" value={String(today.wordsRefreshed)} />
            <Stat label="Articles read" value={String(today.articlesRead)} />
            <Stat
              label="Streak"
              value={`${summary?.currentStreak ?? 0} day${
                (summary?.currentStreak ?? 0) === 1 ? '' : 's'
              }`}
            />
          </div>
          {sessionLine ? (
            <p className="mt-3 text-xs text-muted-foreground">{sessionLine}</p>
          ) : null}
        </div>

        {growthHighlightMessage ? (
          <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
            {growthHighlightMessage}
          </p>
        ) : null}

        <div>
          <h3 className="text-sm font-semibold text-foreground">What would you like to do next?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Guided minimum done — everything you do now still counts toward today.
          </p>
        </div>

        {suggestionsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : suggestionsQuery.isError ? (
          <p className="text-sm text-muted-foreground">
            Could not load suggestions. Use the sidebar to keep exploring.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {suggestionsQuery.data?.suggestions.map((suggestion) => {
              const Icon = suggestionIcons[suggestion.code] ?? ArrowRight
              return (
                <button
                  key={suggestion.code}
                  type="button"
                  className="flex min-h-12 items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                  data-testid={`keep-learning-suggestion-${suggestion.code}`}
                  onClick={() => handleSuggestion(suggestion)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-foreground">{suggestion.title}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {suggestion.description}
                    </span>
                    <span className="mt-1 block text-xs font-medium text-primary">
                      ~{suggestion.estimatedMinutes} min
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        )}

        <div
          className="rounded-xl border border-border/80 bg-muted/30 px-4 py-4 text-sm"
          data-testid="momentum-tomorrow-preview"
        >
          <p className="font-medium text-foreground">Tomorrow</p>
          <p className="mt-1 text-muted-foreground">
            {tomorrowPreviewMessage ??
              (tomorrowDueCount > 0
                ? `${tomorrowDueCount} word${tomorrowDueCount === 1 ? '' : 's'} ready to refresh.`
                : 'We will pick up where you left off.')}
          </p>
        </div>

        {summary?.streakUpdated ? (
          <p className="text-center text-sm text-emerald-600 dark:text-emerald-400">
            Streak updated for today.
          </p>
        ) : null}

        <QuietFeedbackCta testId="keep-learning-feedback-cta" />

        <Button
          className="min-h-12 w-full gap-2"
          onClick={handleDone}
          data-testid="momentum-done"
        >
          Back to Today
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border p-4 text-center sm:text-left">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
    </div>
  )
}
